import axios from "axios";
import type {
  AxiosResponse,
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosInstance,
} from "axios";
import { HttpStatus } from "../types/axios/httpStatus";
import type {
  ResponseModel,
  UploadFileItemModel,
  UploadRequestConfig,
} from "../types/axios/model";
// import router from "@/routers";
import toaster from "@/utils/toaster";
import { useToken } from "@/hooks/useToken";
const { getToken, removeToken } = useToken();
import CryptoJS from "crypto-js";
import router from "@/router";

const VITE_APP_BASE_URL = "http://localhost:8000";

// Add custom request config interface
interface CustomRequestConfig extends AxiosRequestConfig {
  encrypt?: boolean;
}

class HttpRequest {
  service: AxiosInstance;
  private ENCRYPTION_KEY: string;

  constructor() {
    this.ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || "";
    if (!this.ENCRYPTION_KEY) {
      console.error("ENCRYPTION_KEY is not defined in environment variables");
    }
    this.service = axios.create({
      baseURL: "/admin-api",
      timeout: 30 * 1000,
      withCredentials: true, // 允许发送和接收 Cookie
    });

    this.service.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = getToken();
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        }
        config.headers["Access-Control-Allow-Origin"] = "*";
        const existingContentType =
          config.headers["Content-Type"] || config.headers["content-type"];
        if (!existingContentType) {
          config.headers["Content-Type"] = "application/json;charset=utf-8";
        }

        // Add encryption handling
        const customConfig = config as unknown as CustomRequestConfig;
        const normalizedContentType = String(
          config.headers["Content-Type"] || config.headers["content-type"] || ""
        ).toLowerCase();
        if (customConfig.encrypt &&
          config.data &&
          !normalizedContentType.includes("multipart/form-data") &&
          this.ENCRYPTION_KEY) {
          const { encryptedData, iv } = this.encryptData(config.data);
          config.data = {
            data: encryptedData,
            iv: iv
          };
        }

        return config;
      },
      (error: AxiosError) => {
        console.log("requestError: ", error);
        return Promise.reject(error);
      },
      {
        synchronous: true,
        runWhen: (config: InternalAxiosRequestConfig) => {
          return true;
        },
      }
    );

    this.service.interceptors.response.use(
      (response: AxiosResponse<ResponseModel>): AxiosResponse["data"] => {
        if (response.config.responseType === "blob" || response.data instanceof Blob) {
          return response.data as unknown as AxiosResponse["data"];
        }

        const { data } = response;

        // 只有当存在 iv 和加密的 data 时才进行解密
        if (data.iv && data.data) {
          try {
            const decryptedStr = this.decryptData(data.data, data.iv);
            // 将解密后的 JSON 字符串解析为对象
            data.data = JSON.parse(decryptedStr);
          } catch (error) {
            console.error("Response decryption failed:", error);
            data.data = null;
            data.message = "Data decryption failed";
            data.status = HttpStatus.HTTP_500_INTERNAL_SERVER_ERROR;
          }
        }

        const { status } = data;

        if (response.status === HttpStatus.HTTP_401_UNAUTHORIZED) {
        }

        if (status) {
          if (status != HttpStatus.HTTP_200_OK) {
            switch (status) {
              case HttpStatus.HTTP_401_UNAUTHORIZED:
                toaster.error("您的登录状态已过期或被挤下线,请重新登录");
                setTimeout(() => {
                  router.push("/login");
                }, 2000);
                break;
              case HttpStatus.HTTP_403_FORBIDDEN:
                toaster.error(data.message || "您没有权限访问该资源");
                break;
              case HttpStatus.HTTP_404_NOT_FOUND:
                toaster.error(data.message || "资源不存在");
                break;
              case HttpStatus.HTTP_400_BAD_REQUEST:
                toaster.error(data.message || "请求参数错误");
                break;
              default:
                toaster.error(data.message || "操作失败");
                break;
            }
            // return Promise.reject(data.message);
            return data;
          } else {
            return data;
          }
        } else {
          return Promise.reject("Error! code missing!");
        }
      },
      // request status
      (error: AxiosError) => {
        if (error.response) {
          const { status } = error.response;
          switch (status) {
            case HttpStatus.HTTP_401_UNAUTHORIZED:
              removeToken();
              toaster.error("您的登录状态已过期或被挤下线,请重新登录");
              setTimeout(() => {
                window.location.href = "/login";
              }, 2000);
              break;
            default:
              break;
          }
          return Promise.reject(error);
        }
      }
    );
  }

  private encryptData(data: any): { encryptedData: string; iv: string } {
    try {
      // Generate random IV
      const iv = CryptoJS.lib.WordArray.random(16);
      const ivBase64 = CryptoJS.enc.Base64.stringify(iv);

      // Prepare the key
      let key = this.ENCRYPTION_KEY;
      if (key.length < 32) {
        key = key.padEnd(32, "\0");
      }
      key = key.slice(0, 32);
      const keyWordArray = CryptoJS.enc.Utf8.parse(key);

      // Convert data to string if it's an object
      const jsonStr = typeof data === 'object' ? JSON.stringify(data) : String(data);

      // Encrypt
      const encrypted = CryptoJS.AES.encrypt(jsonStr, keyWordArray, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });

      return {
        encryptedData: encrypted.toString(),
        iv: ivBase64
      };
    } catch (error) {
      console.error("Encryption error:", error);
      throw new Error(`Encryption failed: ${(error as Error).message}`);
    }
  }

  private decryptData(encryptedData: string, iv: string): string {
    try {
      const ciphertext = CryptoJS.enc.Base64.parse(encryptedData);
      const ivWordArray = CryptoJS.enc.Base64.parse(iv);

      let key = this.ENCRYPTION_KEY;
      if (key.length < 32) {
        key = key.padEnd(32, "\0");
      }
      key = key.slice(0, 32);
      const keyWordArray = CryptoJS.enc.Utf8.parse(key);

      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: ciphertext },
        keyWordArray,
        {
          iv: ivWordArray,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      );

      const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

      if (!decryptedStr) {
        throw new Error("Decryption failed: Empty result");
      }

      return decryptedStr;
    } catch (error) {
      console.error("Decryption error:", error);
      throw new Error(`Decryption failed: ${(error as Error).message}`);
    }
  }

  request<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    /**
     * 处理 Mock API 请求
     * 如果 URL 以 /mock-api 开头，临时修改 baseURL 为空，使用完整路径
     */
    if (config.url?.startsWith('/mock-api')) {
      config.baseURL = '';
    }

    /**
     * 处理 Dictation API 请求
     * 如果 URL 以 /dictation 开头，临时修改 baseURL 为空，使用完整路径
     */
    if (config.url?.startsWith('/dictation')) {
      config.baseURL = '';
    }

    return new Promise((resolve, reject) => {
      try {
        this.service
          .request<ResponseModel<T>>(config)
          .then((res: AxiosResponse["data"]) => {
            resolve(res as ResponseModel<T>);
          })
          .catch((err) => {
            // do something
            reject(err);
          });
      } catch (err) {
        return Promise.reject(err);
      }
    });
  }

  get<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "GET", ...config });
  }

  post<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "POST", ...config });
  }

  put<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "PUT", ...config });
  }

  patch<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "PATCH", ...config });
  }

  delete<T = any>(config: CustomRequestConfig): Promise<ResponseModel<T>> {
    return this.request({ method: "DELETE", ...config });
  }

  upload<T = string>(
    fileItem: UploadFileItemModel,
    config?: UploadRequestConfig
  ): Promise<ResponseModel<T>> | null {
    if (!VITE_APP_BASE_URL) return null;

    let fd = new FormData();
    fd.append(fileItem.name, fileItem.value);
    let configCopy: UploadRequestConfig;
    if (!config) {
      configCopy = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
    } else {
      config.headers!["Content-Type"] = "multipart/form-data";
      configCopy = config;
    }
    return this.request({
      url: VITE_APP_BASE_URL,
      data: fd,
      ...configCopy,
    });
  }
}

const httpRequest = new HttpRequest();
export default httpRequest;
